import { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { LayoutDashboard, CalendarClock, Users, FileText, Flame, Wallet, Settings, LogOut, Menu, ExternalLink, Route, ShieldCheck, Receipt, Stethoscope, UserCog, Truck, FileCheck, ClipboardList, Smartphone, CalendarDays, Factory } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { BRAND, LOGO } from "@/lib/brand";
import { Sheet, SheetContent } from "@/components/ui/sheet";

const NAV = [
  { to: "/admin", label: "Panoramica", icon: LayoutDashboard, end: true, id: "overview" },
  { to: "/admin/agenda", label: "Agenda ritiri", icon: CalendarClock, id: "agenda" },
  { to: "/admin/anagrafica", label: "Anagrafica", icon: Users, id: "anagrafica" },
  { to: "/admin/ddt", label: "Archivio DDT", icon: FileText, id: "ddt" },
  { to: "/admin/ddt-asl", label: "DDT ASL", icon: ShieldCheck, id: "ddt-asl" },
  { to: "/admin/fatture", label: "Fatture", icon: Receipt, id: "fatture", admin: true },
  { to: "/admin/partner", label: "Veterinari e agenzie", icon: Stethoscope, id: "partner" },
  { to: "/admin/cremazioni", label: "Cremazioni", icon: Flame, id: "cremazioni" },
  { to: "/admin/impianto", label: "Impianto", icon: Factory, id: "impianto" },
  { to: "/admin/riconsegne", label: "Riconsegne e giro", icon: Route, id: "riconsegne" },
  { to: "/admin/ordini", label: "Ordini cliente", icon: ClipboardList, id: "ordini" },
  { to: "/admin/clienti-app", label: "Clienti app e catalogo", icon: Smartphone, id: "clienti-app" },
  { to: "/admin/automezzi", label: "Automezzi", icon: Truck, id: "automezzi" },
  { to: "/admin/autorizzazioni", label: "Autorizzazioni", icon: FileCheck, id: "autorizzazioni" },
  { to: "/admin/contabilita", label: "Contabilità", icon: Wallet, id: "contabilita", admin: true },
  { to: "/admin/dipendenti", label: "Dipendenti", icon: UserCog, id: "dipendenti", admin: true },
  { to: "/admin/dipendenti", label: "Ferie e documenti", icon: CalendarDays, id: "staff", staff: true },
  { to: "/admin/impostazioni", label: "Impostazioni", icon: Settings, id: "impostazioni", admin: true },
];

const SideNav = ({ onNav }) => {
  const { logout, user } = useAuth();
  const isAdmin = user.role === "admin";
  const items = NAV.filter((n) => (isAdmin ? !n.staff : !n.admin));
  return (
    <div className="flex h-full flex-col bg-[#082535] text-white">
      <div className="p-5">
        <div className="bg-[#FAF8F5] rounded-2xl px-4 py-3"><img src={LOGO} alt={BRAND.name} className="h-9 w-auto" /></div>
        {!isAdmin && <p className="text-[10px] uppercase tracking-[0.2em] text-[#F4E8C1] mt-3 px-1">Accesso dipendente · {user.name}</p>}
      </div>
      <nav className="flex-1 px-3 space-y-0.5 overflow-y-auto">
        {items.map(({ to, label, icon: Icon, end, id }) => (
          <NavLink key={id} to={to} end={end} onClick={onNav} data-testid={`admin-nav-${id}`}
            className={({ isActive }) => `flex items-center gap-3 rounded-xl px-4 py-2 text-sm transition-colors ${isActive ? "bg-white/10 text-[#F4E8C1] font-semibold" : "text-white/70 hover:text-white hover:bg-white/5"}`}>
            <Icon className="h-4 w-4" /> {label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 space-y-1 border-t border-white/10">
        <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-3 rounded-xl px-4 py-2 text-sm text-white/70 hover:text-white"><ExternalLink className="h-4 w-4" /> Vedi sito pubblico</a>
        <button onClick={logout} data-testid="admin-logout-button" className="w-full flex items-center gap-3 rounded-xl px-4 py-2 text-sm text-white/70 hover:text-white"><LogOut className="h-4 w-4" /> Esci</button>
      </div>
    </div>
  );
};

export default function AdminLayout() {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-[#FAF8F5] lg:pl-64" data-testid="admin-layout">
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-64 z-30"><SideNav /></aside>
      <div className="lg:hidden sticky top-0 z-30 bg-white/90 backdrop-blur-xl border-b border-[#E2DDD5] h-16 px-4 flex items-center justify-between">
        <img src={LOGO} alt={BRAND.name} className="h-8 w-auto" />
        <button onClick={() => setOpen(true)} data-testid="admin-mobile-menu-button" className="p-2 text-[#0D3B52]" aria-label="Menu"><Menu /></button>
      </div>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="p-0 w-72 border-0"><SideNav onNav={() => setOpen(false)} /></SheetContent>
      </Sheet>
      <main className="px-4 sm:px-8 lg:px-12 py-8 lg:py-12 max-w-[1400px]">
        <Outlet />
      </main>
    </div>
  );
}
